import {
    buildDenialGroupRows,
    DEFAULT_DENIALS_RANGE,
    DenialsRange,
    denialsReportWindow,
    formatDate,
    IDenialGroupRow,
} from '@enter/core';
import { action, makeObservable, observable, runInAction } from 'mobx';
import { DenialsService } from './denials.service';
import type { IFeatures } from './root.store';

export class DenialsStore {
    /** The date range above the table — the only axis the adjustment report offers. */
    @observable public range: DenialsRange = DEFAULT_DENIALS_RANGE;
    @observable public rows: IDenialGroupRow[] = [];
    /** Report items that could not be read as a denial code. Surfaced, never silently dropped. */
    @observable public skipped: number = 0;
    @observable public loading: boolean = false;
    @observable public loaded: boolean = false;
    @observable public error: string | undefined;
    /** When the report on screen was requested — the page's "updated" stamp. Unset until one has loaded. */
    @observable public loadedAt: Date | undefined;

    private service: DenialsService = new DenialsService();
    private features: IFeatures;
    /** Which load is the latest. A slower response for a range already left behind must not overwrite the table. */
    private requestId = 0;

    constructor(features: IFeatures) {
        this.features = features;
        makeObservable(this);
    }

    @action
    public setRange = (range: DenialsRange): void => {
        if (range === this.range) {
            return;
        }
        this.range = range;
        void this.load();
    };

    @action
    public load = async (now: Date = new Date()): Promise<void> => {
        // Demo mode has no denial generator yet, and the NS-14 boundary refuses the request. Say
        // that plainly instead of letting the refusal surface as a generic failure — a salesperson
        // seeing "something went wrong" cannot tell a missing feature from a broken one.
        if (this.features.isDemo) {
            runInAction(() => {
                this.rows = [];
                this.skipped = 0;
                this.loaded = true;
                this.error = 'Denials are not generated in demo mode yet.';
            });
            return;
        }

        runInAction(() => {
            this.loading = true;
            this.error = undefined;
        });

        const { start, end } = denialsReportWindow(this.range, now);
        const request = ++this.requestId;

        try {
            const items = await this.service.fetchAdjustmentReport({
                start: formatDate(start, 'yyyy-MM-dd'),
                end: formatDate(end, 'yyyy-MM-dd'),
            });
            if (request !== this.requestId) {
                return;
            }
            const { rows, skipped } = buildDenialGroupRows(items);
            runInAction(() => {
                this.rows = rows;
                this.skipped = skipped;
                this.loadedAt = now;
                this.loaded = true;
                this.loading = false;
            });
        } catch (err) {
            if (request !== this.requestId) {
                return;
            }
            runInAction(() => {
                this.error = err instanceof Error ? err.message : 'Failed to load the adjustment report';
                this.loading = false;
                this.loaded = true;
            });
        }
    };
}
